import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useAccessToken } from '../auth/useAccessToken';
import { createApiClient } from './client';
import type { ApiClient } from './client';

const ApiContext = createContext<ApiClient | null>(null);

/** Builds one API client per session from the MSAL token getter. Tests pass `client` directly. */
export function ApiProvider({ children, client }: { children: ReactNode; client?: ApiClient }) {
  const getToken = useAccessToken();
  const value = useMemo(() => client ?? createApiClient({ getToken }), [client, getToken]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/** Variant for tests and storybook-style harnesses that have no MSAL context. */
export function StaticApiProvider({ children, client }: { children: ReactNode; client: ApiClient }) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used inside <ApiProvider>');
  return ctx;
}
